<template>
  <v-card>
    <v-toolbar flat dark color="primary" dense>
      <v-toolbar-title class="font-weight-light">
        {{ incident.vehicleLabel }} &mdash; {{ formatTimestamp(incident.timestamp) }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-3">
      <v-row>
        <v-col cols="12" sm="4">
          <v-list dense>
            <v-subheader class="font-weight-bold">{{ tv('Recordings') }}</v-subheader>
            <v-list-item
              v-for="rec in incident.recordings"
              :key="rec.id"
              :class="{ 'primary lighten-5': selectedRecording && selectedRecording.id === rec.id }"
              @click="selectRecording(rec)"
              style="cursor: pointer;"
            >
              <v-list-item-icon>
                <v-icon :color="selectedRecording && selectedRecording.id === rec.id ? 'primary' : ''">
                  mdi-video
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ channelLabel(rec.fileName) }}</v-list-item-title>
                <v-list-item-subtitle v-if="rec.durationSeconds">
                  {{ rec.durationSeconds }}s
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>

        <v-col cols="12" sm="8" class="d-flex align-center justify-center">
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2 caption">{{ tv('Loading video') }}</div>
          </div>
          <div v-else-if="errorMessage" class="text-center py-8">
            <v-icon large color="red">mdi-alert-circle</v-icon>
            <div class="mt-2 caption red--text">{{ errorMessage }}</div>
          </div>
          <div v-else-if="videoUrl" style="width: 100%;">
            <video
              :src="videoUrl"
              controls
              preload="auto"
              style="width: 100%; max-height: 350px; background: #000;"
              :key="videoUrl"
              @error="handleVideoError"
            ></video>
            <div class="text-right mt-1">
              <a :href="videoUrl" target="_blank" rel="noopener" class="caption grey--text">
                <v-icon x-small class="grey--text">mdi-open-in-new</v-icon> {{ tv('Open in new tab') }}
              </a>
            </div>
          </div>
          <div v-else class="text-center py-8 grey--text">
            <v-icon large>mdi-play-circle-outline</v-icon>
            <div class="mt-2 caption">{{ tv('Select a recording to play') }}</div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import httpClient from '@/api/httpClient'
import formatDate from '@/utils/formatDateHelper.js'
import translate from '@/utils/genTable/multilanguageHelper.js'

export default {
  name: 'VideoPlayerDialog',
  props: {
    incident: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedRecording: null,
      videoUrl: null,
      loading: false,
      errorMessage: null,
      urlCache: {}
    }
  },
  mounted() {
    if (this.incident.recordings && this.incident.recordings.length === 1) {
      this.selectRecording(this.incident.recordings[0])
    }
  },
  methods: {
    tv(key) {
      return translate(key, 'pages.video')
    },
    channelLabel(fileName) {
      if (!fileName) return this.tv('Recording')
      const parts = fileName.split('_')
      // format: YYYYMMDD_HHMMSS_EventType_CameraID.ext
      // CameraID 1 = Channel 1 (prednja kamera - put)
      // CameraID 2 = Channel 2 (unutrašnja kamera - vozač)
      const cameraId = parts[3] ? parts[3].replace(/\.[^.]+$/, '') : null
      if (cameraId === '1') return this.tv('Road camera')
      if (cameraId === '2') return this.tv('Interior camera')
      return this.tv('Recording')
    },
    formatTimestamp(ts) {
      if (!ts) return '-'
      return formatDate(ts, 'medium')
    },
    async selectRecording(rec) {
      this.selectedRecording = rec
      this.videoUrl = null
      this.errorMessage = null

      const cached = this.urlCache[rec.id]
      if (cached && Date.now() - cached.fetchedAt < 55 * 60 * 1000) {
        this.videoUrl = cached.url
        return
      }

      this.loading = true
      try {
        const response = await httpClient.get(`camera/recordings/${rec.id}/signed-url`)
        this.urlCache[rec.id] = { url: response.data.url, fetchedAt: Date.now() }
        this.videoUrl = response.data.url
      } catch (e) {
        this.errorMessage = this.tv('Failed to load video URL')
      } finally {
        this.loading = false
      }
    },
    handleVideoError(e) {
      const video = e.target
      const err = video.error
      const messages = {
        1: this.tv('Playback aborted'),
        2: this.tv('Network error'),
        3: this.tv('Decode error'),
        4: this.tv('Source not supported')
      }
      this.errorMessage = err
        ? (messages[err.code] || `Video error code ${err.code}`)
        : this.tv('Video failed to load')
      this.videoUrl = null
    }
  }
}
</script>
